import CancelOrderDialogActionButtons from '@/components/OrdersModule/OrderDetail/CancelOrderDialog/CancelOrderDialogActionButtons'
import CancelOrderDialogReasonRadioButtons from '@/components/OrdersModule/OrderDetail/CancelOrderDialog/CancelOrderDialogReasonRadioButtons'
import { useCancelOrderDialogSubmission } from '@/components/OrdersModule/OrderDetail/CancelOrderDialog/hooks/useCancelOrderDialogSubmission'
import CancelOrderDialogReturnMoneyRadioButtons from '@/components/OrdersModule/OrderDetail/CancelOrderDialog/parts/ReturnMoneyRadioButtons'
import NoteSwitcher from '@/features/common/components/InsertNoteFormContent/Switcher/parts'
import Pill from '@/features/ui/components/Pill'
import Typography from '@/features/ui/typography'
import { CancelOrderReason } from '@/types/cancelOrder'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollView, StyleSheet, View } from 'react-native'

interface CancelOrderDialogOptionsFormProps {
    uuid: string
    reason: CancelOrderReason
    returnMoney: boolean
    confirmedNote: string | null
    onReasonChange: (reason: CancelOrderReason) => void
    onReturnMoneyChange: (returnMoney: boolean) => void
    onDismiss: () => void
    onNoteButtonPress: () => void
}

const CancelOrderDialogOptionsForm: FC<CancelOrderDialogOptionsFormProps> = ({
    uuid,
    reason,
    returnMoney,
    confirmedNote,
    onReasonChange,
    onReturnMoneyChange,
    onDismiss,
    onNoteButtonPress,
}) => {
    const { t } = useTranslation()
    const { loading, handleConfirm, handleDismiss, handleNoteButtonPress } =
        useCancelOrderDialogSubmission({
            uuid,
            reason,
            returnMoney,
            confirmedNote,
            onDismiss,
            onNoteButtonPress,
        })

    return (
        <ScrollView
            contentContainerStyle={styles.container}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
        >
            <Typography.Heading variant="h3" style={{ alignSelf: 'center' }}>
                {t('order.detail.cancelDialog.optionsFormTitle')}
            </Typography.Heading>
            <View style={{ gap: 8 }}>
                <View style={styles.labelRow}>
                    <Typography.Heading variant="h4">
                        {t('order.detail.cancelDialog.reasonLabel')}
                    </Typography.Heading>
                    <Pill color="destructive">{t('form.requiredFieldLabel')}</Pill>
                </View>
                <CancelOrderDialogReasonRadioButtons
                    disabled={loading}
                    reason={reason}
                    onReasonChange={onReasonChange}
                />
            </View>
            <View style={{ gap: 8 }}>
                <View style={styles.labelRow}>
                    <Typography.Heading variant="h4">
                        {t('order.detail.cancelDialog.returnMoneyLabel')}
                    </Typography.Heading>
                    <Pill color="destructive">{t('form.requiredFieldLabel')}</Pill>
                </View>
                <CancelOrderDialogReturnMoneyRadioButtons
                    disabled={loading}
                    returnMoney={returnMoney}
                    onReturnMoneyChange={onReturnMoneyChange}
                />
            </View>
            <View pointerEvents={loading ? 'none' : 'auto'} style={loading && styles.disabled}>
                <NoteSwitcher confirmedNote={confirmedNote} onEdit={handleNoteButtonPress} />
            </View>
            <CancelOrderDialogActionButtons
                confirmLoading={loading}
                onConfirm={handleConfirm}
                onCancel={handleDismiss}
            />
        </ScrollView>
    )
}

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 16,
        paddingVertical: 24,
        gap: 24,
    },
    labelRow: {
        alignItems: 'center',
        flexDirection: 'row',
        gap: 8,
    },
    disabled: {
        opacity: 0.5,
    },
})

export default CancelOrderDialogOptionsForm
